/**
 * What a voice call reads off its session (RFC 0307 3b to 3d), pure over the
 * raw documents, so the call can keep reading the session with no thread on
 * screen.
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import {
  type InteractionAnswerValue,
  parseQuestionBody,
  projectSessionActiveRun,
  projectSessionPendingInteractions,
  projectSessionStripActivity,
  sessionGoalTask,
  type TranscriptMessage,
} from '../../model'
import { registryTasks, sessionTaskGroups } from '../../model/tasks/registry'
import { answerInteraction } from '../run/public'
import type { SessionVoiceState } from './plan'

/** The text of a log value: a string, or the `ImmutableString` the log keeps. */
function argString(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  const inner = (value as { val?: unknown } | null)?.val
  return typeof inner === 'string' ? inner : undefined
}

/** A tool's name without its host's prefix: `mcp__mod__x`, `mod__x`, `mod.x`. */
function bareToolName(name: string): string {
  return name.replace(/^mcp__[^_]+(?:_[^_]+)*?__/, '').replace(/^mod[._]/, '')
}

/** What `session_state` answers, and what `voiceNotices` compares. */
export function sessionVoiceState(
  sessionDoc: unknown,
  projectDoc: unknown,
  sessionId: string,
  transcript: readonly TranscriptMessage[],
): SessionVoiceState {
  const active = projectSessionActiveRun(sessionDoc)
  const pending = projectSessionPendingInteractions(sessionDoc).find(
    (i) => i.kind === 'question',
  )
  const body = pending ? parseQuestionBody(pending.bodyJson) : null
  const q = body?.questions[0]
  const recommended = (body as { recommended?: unknown } | null)?.recommended
  const goal = sessionGoalTask(registryTasks(projectDoc), sessionId)
  const subtasks = sessionTaskGroups(projectDoc, sessionId).flatMap((g) =>
    g.tasks.map((t) => ({ title: String(t.title), status: String(t.status) })),
  )
  let lastReply: string | undefined
  for (let i = transcript.length - 1; i >= 0; i--) {
    const m = transcript[i]
    if (m?.role !== 'assistant') continue
    const text = m.content
      .map((b) => (b.type === 'text' ? String(b.text) : ''))
      .join('')
      .trim()
    if (text) {
      lastReply = text
      break
    }
  }
  const recent = recentConversation(transcript)
  // The live line the window draws mid-turn (RFC 0304 3h).
  const strip = active
    ? projectSessionStripActivity(sessionDoc, 'running')
    : null
  const live =
    strip?.kind === 'text'
      ? { kind: 'text' as const, text: String(strip.text) }
      : strip?.kind === 'tool'
        ? {
            kind: 'tool' as const,
            text: strip.summary
              ? `${String(strip.name)} ${String(strip.summary)}`
              : String(strip.name),
          }
        : undefined
  return {
    ...(live ? { live } : {}),
    status: pending
      ? 'asking'
      : active
        ? active.claimed
          ? 'running'
          : 'queued'
        : 'idle',
    ...(goal ? { goal: String(goal.title) } : {}),
    subtasks,
    ...(lastReply ? { lastReply } : {}),
    ...(recent.length ? { recent } : {}),
    ...(pending && q
      ? {
          question: {
            interactionId: String(pending.interactionId),
            prompt: String(q.prompt),
            options: q.options.map((o) => String(o.label)),
            ...(typeof recommended === 'string' ? { recommended } : {}),
            // Every part, when it asks more than one thing: the voice must ask
            // them all and answer them together.
            ...(body && body.questions.length > 1
              ? {
                  parts: body.questions.map((part) => ({
                    prompt: String(part.prompt),
                    options: part.options.map((o) => String(o.label)),
                  })),
                }
              : {}),
          },
        }
      : {}),
  }
}

/** How much of the thread the voice is handed, and how much of each line. */
const RECENT_LINES = 8
const RECENT_CHARS = 4000

/**
 * The thread's typed conversation, oldest first: the person's messages and the
 * agent's text replies. Spoken lines are left out, since the call already has
 * them, and so are tool rows, which are the agent's working, not what it said.
 */
function recentConversation(
  transcript: readonly TranscriptMessage[],
): Array<{ by: 'person' | 'agent'; text: string }> {
  const out: Array<{ by: 'person' | 'agent'; text: string }> = []
  for (
    let i = transcript.length - 1;
    i >= 0 && out.length < RECENT_LINES;
    i--
  ) {
    const m = transcript[i]
    if (!m || m.spoken) continue
    if (m.role !== 'user' && m.role !== 'assistant') continue
    const text = m.content
      .map((b) => (b.type === 'text' ? String(b.text) : ''))
      .join('')
      .trim()
    if (!text) continue
    out.push({
      by: m.role === 'user' ? 'person' : 'agent',
      text:
        text.length > RECENT_CHARS ? `${text.slice(0, RECENT_CHARS)}…` : text,
    })
  }
  return out.reverse()
}

/**
 * Answer the waiting question with what the person said: an option when they
 * named one, otherwise their words as the free-text answer. A question with
 * several parts takes one answer per part, in order, and is refused without
 * all of them: half an answer cannot be sent and kept.
 */
export async function answerSpoken(
  o: { repo: Repo; sessionId: string; projectId?: string; sessionDoc: unknown },
  text: string,
  parts?: readonly string[],
): Promise<boolean> {
  const pending = projectSessionPendingInteractions(o.sessionDoc).find(
    (i) => i.kind === 'question',
  )
  if (!pending) return false
  const body = parseQuestionBody(pending.bodyJson)
  const q = body.questions[0]
  const option = q ? spokenOption(q.options, text) : undefined
  const answers: Record<string, InteractionAnswerValue> | null =
    body.questions.length > 1
      ? partAnswers(body.questions, parts)
      : q && option
        ? { [q.id]: { selected: [String(option.value)] } }
        : body.questions.length === 1 && q && text.trim()
          ? { [q.id]: { selected: [], freeText: text.trim() } }
          : null
  if (!answers) return false
  return answerInteraction(
    o.repo,
    o.sessionId,
    pending.interactionId,
    answers,
    {
      ...(o.projectId ? { projectId: o.projectId } : {}),
    },
  )
}

/** One answer per part, in order: the option it names, or its own words. */
function partAnswers(
  questions: ReturnType<typeof parseQuestionBody>['questions'],
  parts: readonly string[] | undefined,
): Record<string, InteractionAnswerValue> | null {
  const said = (parts ?? []).map((p) => p.trim())
  if (said.length !== questions.length || said.some((p) => !p)) return null
  const answers: Record<string, InteractionAnswerValue> = {}
  questions.forEach((part, i) => {
    const text = said[i] as string
    const option = spokenOption(part.options, text)
    answers[part.id] = option
      ? { selected: [String(option.value)] }
      : { selected: [], freeText: text }
  })
  return answers
}

/**
 * The option a spoken answer names: an exact label first, then the LONGEST
 * label said as whole words, so "postgres with pgvector" is not taken for
 * "Postgres". Two labels said and neither containing the other is ambiguous,
 * and goes as the person's own words instead.
 */
export function spokenOption<O extends { label: unknown }>(
  options: readonly O[],
  said: string,
): O | undefined {
  const words = ` ${said
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()} `
  const norm = (o: O) =>
    String(o.label)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
  const exact = options.find((o) => ` ${norm(o)} ` === words)
  if (exact) return exact
  const named = options
    .filter((o) => norm(o) && words.includes(` ${norm(o)} `))
    .sort((a, b) => norm(b).length - norm(a).length)
  const [first, second] = named
  if (!first) return undefined
  if (second && !norm(first).includes(norm(second))) return undefined
  return first
}

/**
 * The sessions this one handed work to (RFC 0307 3d), newest first.
 *
 * A call's own agent often does not do the work itself: it starts a session
 * or sends one a message, and the work happens there. A voice that read only
 * its own session would call itself idle while that work runs. So the call
 * follows the sessions named by `send_message` (its argument) and by
 * `start_session` (the id its result reports).
 */
export function delegatedSessionIds(
  transcript: readonly TranscriptMessage[],
  max = 3,
): string[] {
  const results = new Map<string, string>()
  for (const m of transcript) {
    if (m.role === 'tool_result' && m.toolUseId)
      results.set(
        String(m.toolUseId),
        m.content
          .map((b) => (b.type === 'text' ? String(b.text) : ''))
          .join(''),
      )
  }
  const ids: string[] = []
  for (let i = transcript.length - 1; i >= 0 && ids.length < max; i--) {
    const m = transcript[i]
    if (m?.role !== 'assistant') continue
    for (let j = m.content.length - 1; j >= 0; j--) {
      const b = m.content[j]
      if (b?.type !== 'tool_use') continue
      const name = bareToolName(String(b.name))
      let id = ''
      if (name === 'send_message') {
        const a = (b.arguments ?? {}) as Record<string, unknown>
        id = argString(a.sessionId) ?? argString(a.session_id) ?? ''
      } else if (name === 'start_session') {
        const said = results.get(String(b.id)) ?? ''
        id = /(?:opened|branched) (\S+) \(/.exec(said)?.[1] ?? ''
      }
      id = id.trim()
      if (id && !ids.includes(id)) ids.push(id)
      if (ids.length >= max) break
    }
  }
  return ids
}
